import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn, setIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson, InlandMarineTypes as defaultInlandMarineTypes } from '@ourbranch/lookups';
import { FarmScheduleCoverageType } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import { getMaskedInlandMarineTypeOptions, MISC_BOAT_CLASSIFICATION, isMiscBoat } from 'common/helpers/farm-helpers';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import Field from 'core/components/form/form.v2';
import { AppliedTag } from 'common/components/farm/applied-tag';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from './collapsible-list-item';
import Grid from '@material-ui/core/Grid';
import alertIcon from 'core/assets/svg/alert-white-bold.svg';

import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import useStyles from './sub-risk.styles';
import { BrandFormField } from 'common/components/farm/brand-form-field';
import { hasErrorMessages } from './sub-risk.utils';

const defaultInlandMarineTypesOptions = Object.values(defaultInlandMarineTypes).map((type) => ({
  id: type.id,
  value: type.value
}));

// Utility function to strip GraphQL __typename fields to prevent false positive offer differences
const stripTypenames = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(stripTypenames);
  }
  if (obj && typeof obj === 'object') {
    const cleaned = {};
    Object.keys(obj).forEach((key) => {
      if (key !== '__typename') {
        cleaned[key] = stripTypenames(obj[key]);
      }
    });
    return cleaned;
  }
  return obj;
};

// #34019: Gator/Utility Transport Vehicle mirrors ATV in every way (same fields, same cc dropdown,
// same rating). ATV_LIKE_TYPES keeps the two in lockstep across the field/validation lists below.
const GATOR = 'gatorUtilityTransportVehicle';
const ATV_LIKE_TYPES = ['atv', GATOR];
const isAtvLike = (category) => ATV_LIKE_TYPES.includes(category);

const typesRequiringYear = ['boat', 'atv', GATOR, 'golfCart', 'unmannedAircraft'];
const typesRequiringMake = ['golfCart', 'atv', GATOR, 'unmannedAircraft'];
const typesRequiringModel = ['golfCart', 'atv', GATOR, 'unmannedAircraft'];
const typesRequiringSerialNumber = ['boat', 'unmannedAircraft', 'atv', GATOR, 'golfCart'];

// #33277: Watercraft/Boats with the "Miscellaneous" classification (boatClassifications id `misc`,
// filed as "Miscellaneous Boating Equipment") is not a specific boat, so only name, description, the
// all-peril deductible and the coverage limit are required — the boat-specific fields (year,
// horsepower, length, speed, serial number) are neither required nor shown.
// MISC_BOAT_CLASSIFICATION/isMiscBoat live in farm-helpers so this form and the offer-level
// validation schema share one definition (their drift caused #33277/#33279); re-exported here for
// existing importers.
export { MISC_BOAT_CLASSIFICATION, isMiscBoat };
export const MISC_BOAT_TITLE = 'Miscellaneous Boating Equipment';

// The boat-specific fields hidden for a Miscellaneous boat. When an item is (or becomes) a misc
// boat these are cleared so stale values a user entered under another classification aren't
// persisted into extendedDetails (and can't surface on generated documents). #33277
export const BOAT_SPECIFIC_FIELDS = ['horsepower', 'year', 'lengthFt', 'milesPerHour', 'serialNumber'];

export const validationSchema = () =>
  Yup.object().shape({
    category: Yup.string().required('Item type is required'),
    name: Yup.string().required('Item name is required'),
    // #33279: the legacy top-level "Deductible" field has been removed from the UI for ALL states
    // (previously only IL/Forreston hid it) — the canonical "All peril deductible" is stored on
    // extendedDetails.deductibleAllOther. The legacy field is no longer required anywhere.
    deductible: Yup.number().nullable(),
    limit: Yup.number().required('Coverage limit is required'),
    description: Yup.string().nullable().required('Description is required'),
    year: Yup.number().when(['category', 'classification'], {
      is: (category, classification) => typesRequiringYear.includes(category) && !isMiscBoat(category, classification),
      then: (schema) =>
        schema
          .required('Year is required')
          .min(1900, 'Year must be 1900 or later')
          .max(new Date().getFullYear(), `Year must be ${new Date().getFullYear()} or earlier`)
    }),
    horsepower: Yup.number().when(['category', 'classification'], {
      is: (category, classification) => category === 'boat' && !isMiscBoat(category, classification),
      then: (schema) => schema.required('Horsepower is required')
    }),
    serialNumber: Yup.string().when(['category', 'classification'], {
      is: (category, classification) =>
        typesRequiringSerialNumber.includes(category) && !isMiscBoat(category, classification),
      then: (schema) => schema.required('Serial Number is required')
    }),
    classification: Yup.string().when('category', {
      is: 'boat',
      then: (schema) => schema.required('Classification is required')
    }),
    make: Yup.string().when('category', {
      is: (val) => typesRequiringMake.includes(val),
      then: (schema) => schema.required('Make is required')
    }),
    model: Yup.string().when('category', {
      is: (val) => typesRequiringModel.includes(val),
      then: (schema) => schema.required('Model is required')
    }),
    cc: Yup.mixed().when('category', {
      is: (val) => isAtvLike(val),
      then: (schema) => schema.required('CC is required')
    }),
    weight: Yup.number().when('category', {
      is: 'unmannedAircraft',
      then: (schema) => schema.required('Weight is required')
    }),
    lengthFt: Yup.number().when(['category', 'classification'], {
      is: (category, classification) => category === 'boat' && !isMiscBoat(category, classification),
      then: (schema) => schema.required('Length is required')
    }),
    milesPerHour: Yup.number().when(['category', 'classification'], {
      is: (category, classification) => category === 'boat' && !isMiscBoat(category, classification),
      then: (schema) => schema.required('Miles per hour is required')
    }),
    dimensions: Yup.string().when('category', {
      is: 'boatDock',
      then: (schema) => schema.required('Dimensions are required')
    })
  });

// Unique DOM-id namespace for this add form's text inputs. This isolated Formik "add item" form used
// bare DOM ids (name, description, limit, ...), which collided with other independent add-forms on the
// offer page (e.g. the additional-interest form). Duplicate DOM ids made clicking an empty field's
// floating label jump focus to the first matching input higher up the page (#32617). Prefixing keeps
// each DOM id unique; the Formik field `name` stays bare so `onSubmit` still reads the flat values.
const ADD_ID_PREFIX = 'inland-marine-add-';

const alertMessages = {
  atv: 'Remember to add Incidental Liability coverage when an ATV or Related Equipment is included on the policy.',
  // #34019: Gator/Utility Transport Vehicle mirrors the ATV incidental-liability reminder.
  [GATOR]:
    'Remember to add Incidental Liability coverage when a Gator/Utility Transport Vehicle is included on the policy.',
  boat: 'Remember to add Incidental Liability coverage when a boat or watercraft is included on the policy.',
  boatDock: 'Boat docks are excluded from damage caused by collapse of weight of ice, snow or sleet.',
  golfCart: 'When a golf cart is included on the policy, liability coverage must be on and the item must be scheduled.',
  unmannedAircraft: 'Remember to add Incidental Liability coverage when an Unmanned Aircraft is included on the policy.'
};

const InlandMarineForm = ({
  fieldName,
  disabled,
  riskType,
  coverages,
  state: stateProp,
  hideCollapser,
  validateParentOnAdd
}) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  const scheduleCoverages = getIn(values, fieldName) || [];
  const items = scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.SPP)?.items || [];
  const coverageIndex = scheduleCoverages.findIndex((c) => c.coverageType === FarmScheduleCoverageType.SPP);

  // #33277: when an existing item is (or becomes) a Miscellaneous boat, clear any stale boat-specific
  // values so they aren't persisted into extendedDetails or printed on documents. Covers switching an
  // existing item's classification to Miscellaneous in the edit form (the add form strips them on
  // submit). Idempotent: only writes when a stale value is present, so it converges.
  useEffect(() => {
    if (coverageIndex < 0) {
      return;
    }
    items.forEach((item, index) => {
      if (!isMiscBoat(item.category, item.extendedDetails?.classification)) {
        return;
      }
      BOAT_SPECIFIC_FIELDS.forEach((field) => {
        if (item.extendedDetails?.[field] != null) {
          setFieldValue(`${fieldName}[${coverageIndex}].items[${index}].extendedDetails.${field}`, undefined);
        }
      });
    });
  }, [items, coverageIndex, fieldName, setFieldValue]);

  // An explicit `state` prop wins: the fallbacks below read location 0, so a card on any later
  // location resolved the wrong state's brand options, and on the new-offer form the location address
  // may not be populated at all when the items are entered. (#33164)
  const state = stateProp || getIn(values, 'farmLocations.0.address.state') || getIn(values, 'correctedAddress.state');
  // #34019 (finishes #31792 step 2): the ATV / Gator-UTV cc boundary is state-specific. MO (CFM,
  // AAIS) uses a 600cc boundary; other states (e.g. Forreston/IL) fall back to the flat 500cc list.
  // Mirrors the incidental-liability assets section, which already reads ccByState.
  const ccOptions = lookupsJson.farm.ccByState?.[state] || lookupsJson.farm.cc;
  // #32207: route-aware product so Home (Z) / Rental (Y) inland-marine type restrictions apply on
  // the policy route (the offer store is empty there).
  const { farmPolicyType } = useFarmPolicyContext();

  const isInlandMarineCoverageEnabled =
    !appConfig.farm[state]?.risks[riskType]?.hiddenCoverages?.includes('inlandMarine');
  const brandSpecificTypes = getMaskedInlandMarineTypeOptions(
    farmPolicyType,
    state,
    (
      appConfig.farm[state]?.risks[riskType]?.allowedOptions?.inlandMarineItemTypes || defaultInlandMarineTypesOptions
    ).sort((a, b) => a.id.localeCompare(b.id))
  );
  const brandDeductibleOptions =
    appConfig.farm[state]?.risks[riskType]?.allowedOptions?.inlandMarineDeductible ||
    lookupsJson.farm.inlandMarineDeductible;

  if (!isInlandMarineCoverageEnabled) {
    return null;
  }

  // Auto-expand if there are any scheduled inland marine items
  const hasScheduledItems = items.length > 0;

  // On a standalone inland marine risk the items ARE the whole risk, so the card has exactly one
  // section and the collapser's heading + chevron would be redundant chrome around it — render the
  // items section bare instead. Everywhere else inland marine is one of several sub-risks on a
  // dwelling/FO-6 card and keeps its collapser. A variable wrapper keeps this a one-line branch
  // rather than duplicating the ~400-line section body. (#33164)
  const SectionWrapper = hideCollapser ? React.Fragment : SubRiskCollapsibleSection;
  const sectionWrapperProps = hideCollapser
    ? {}
    : {
        type: 'secondary',
        content: <div className={classes.headerText}>Inland marine coverage</div>,
        contentRight: hasScheduledItems ? <AppliedTag /> : null,
        initialOpen: hasScheduledItems
      };

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <SectionWrapper {...sectionWrapperProps}>
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={items?.length || 0}
          emptyStateTitle="There are no items added yet"
        >
          {/* list items */}
          {items.map((item, index) => {
            // #33277: a boat with the "Miscellaneous" classification is filed as
            // "Miscellaneous Boating Equipment"; use that as the item type label instead of the
            // generic "Watercraft/Boats" type name.
            const typeName = isMiscBoat(item.category, item.extendedDetails?.classification)
              ? MISC_BOAT_TITLE
              : brandSpecificTypes.find((type) => type.id === item.category)?.value;
            const displayName = item.name || typeName;
            const rightSideValues = [];

            // #31790: the canonical inland-marine deductible is the "All peril deductible"
            // stored on extendedDetails.deductibleAllOther. The legacy top-level `deductible`
            // is empty for Forreston (IL) items, so reading it alone made the deductible look
            // missing in details even though it prints on the documents. Match the doc-mapper
            // (#30842): prefer deductibleAllOther, fall back to the legacy field.
            const summaryDeductible = item.extendedDetails?.deductibleAllOther ?? item.deductible;
            if (summaryDeductible) {
              rightSideValues.push({
                label: 'Deductible',
                value: `$${summaryDeductible.toLocaleString('en-US')}`
              });
            }
            rightSideValues.push({ label: 'Limit', value: `$${item.limit?.toLocaleString('en-US') || item.limit}` });

            const itemPath = `${fieldName}[${coverageIndex}].items[${index}]`;
            const category = item.category;

            return (
              <CollapsibleListItem
                key={`item-${index}`}
                title={
                  <div className={classes.itemTitleWrapper}>
                    <div>{displayName}</div>
                    <div className={classes.itemTypeLabel}>{typeName}</div>
                  </div>
                }
                rightSideValues={rightSideValues}
                alertMessage={alertMessages[item.category] ?? null}
                onDelete={() => {
                  const newItems = items.filter((_, checkIndex) => checkIndex !== index);
                  const currentScheduleCoverages =
                    stripTypenames(
                      getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.SPP)
                    ) || [];
                  setFieldValue(`${fieldName}`, [
                    ...currentScheduleCoverages,
                    {
                      coverageType: FarmScheduleCoverageType.SPP,
                      items: newItems
                    }
                  ]);
                  setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.SPP}`);
                }}
                disabled={disabled}
              >
                <Field
                  xs={12}
                  type="string"
                  id={`${itemPath}.name`}
                  name={`${itemPath}.name`}
                  mode="dark"
                  label="Item Name"
                  disabled={disabled}
                />

                {category === 'golfCart' && (
                  <>
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.year`}
                      name={`${itemPath}.extendedDetails.year`}
                      mode="dark"
                      label="Year"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.make`}
                      name={`${itemPath}.extendedDetails.make`}
                      mode="dark"
                      label="Make"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.model`}
                      name={`${itemPath}.extendedDetails.model`}
                      mode="dark"
                      label="Model"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.serialNumber`}
                      name={`${itemPath}.extendedDetails.serialNumber`}
                      mode="dark"
                      label="Serial Number"
                      disabled={disabled}
                    />
                  </>
                )}

                {isAtvLike(category) && (
                  <>
                    <Field
                      xs={4}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.year`}
                      name={`${itemPath}.extendedDetails.year`}
                      mode="dark"
                      label="Year"
                      disabled={disabled}
                    />
                    <Field
                      xs={4}
                      type="string"
                      id={`${itemPath}.extendedDetails.make`}
                      name={`${itemPath}.extendedDetails.make`}
                      mode="dark"
                      label="Make"
                      disabled={disabled}
                    />
                    <Field
                      xs={4}
                      type="string"
                      id={`${itemPath}.extendedDetails.model`}
                      name={`${itemPath}.extendedDetails.model`}
                      mode="dark"
                      label="Model"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.serialNumber`}
                      name={`${itemPath}.extendedDetails.serialNumber`}
                      mode="dark"
                      label="Serial Number"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="select"
                      id={`${itemPath}.extendedDetails.cc`}
                      name={`${itemPath}.extendedDetails.cc`}
                      mode="dark"
                      label="CC"
                      options={ccOptions}
                      disabled={disabled}
                    />
                  </>
                )}

                {category === 'unmannedAircraft' && (
                  <>
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.year`}
                      name={`${itemPath}.extendedDetails.year`}
                      mode="dark"
                      label="Year"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.make`}
                      name={`${itemPath}.extendedDetails.make`}
                      mode="dark"
                      label="Make"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.model`}
                      name={`${itemPath}.extendedDetails.model`}
                      mode="dark"
                      label="Model"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${itemPath}.extendedDetails.serialNumber`}
                      name={`${itemPath}.extendedDetails.serialNumber`}
                      mode="dark"
                      label="Serial Number"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id={`${itemPath}.extendedDetails.weight`}
                      name={`${itemPath}.extendedDetails.weight`}
                      mode="dark"
                      label="Weight (lbs)"
                      disabled={disabled}
                    />
                  </>
                )}

                {category === 'boat' && (
                  <>
                    <Field
                      xs={6}
                      type="select"
                      id={`${itemPath}.extendedDetails.classification`}
                      name={`${itemPath}.extendedDetails.classification`}
                      mode="dark"
                      label="Classification"
                      options={lookupsJson.farm.boatClassifications}
                      disabled={disabled}
                    />
                    {/* #33277: "Miscellaneous Boating Equipment" is not a specific boat, so hide the
                        boat-specific fields (horsepower/year/length/speed/serial). */}
                    {!isMiscBoat(category, item.extendedDetails?.classification) && (
                      <>
                        <Field
                          xs={6}
                          type="numeric"
                          id={`${itemPath}.extendedDetails.horsepower`}
                          name={`${itemPath}.extendedDetails.horsepower`}
                          mode="dark"
                          label="Horsepower"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id={`${itemPath}.extendedDetails.year`}
                          name={`${itemPath}.extendedDetails.year`}
                          mode="dark"
                          label="Year"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id={`${itemPath}.extendedDetails.lengthFt`}
                          name={`${itemPath}.extendedDetails.lengthFt`}
                          mode="dark"
                          label="Length (ft)"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id={`${itemPath}.extendedDetails.milesPerHour`}
                          name={`${itemPath}.extendedDetails.milesPerHour`}
                          mode="dark"
                          label="Miles Per Hour"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id={`${itemPath}.extendedDetails.serialNumber`}
                          name={`${itemPath}.extendedDetails.serialNumber`}
                          mode="dark"
                          label="Serial Number"
                          disabled={disabled}
                        />
                      </>
                    )}
                  </>
                )}

                {category === 'boatDock' && (
                  <Field
                    xs={12}
                    type="string"
                    id={`${itemPath}.extendedDetails.dimensions`}
                    name={`${itemPath}.extendedDetails.dimensions`}
                    mode="dark"
                    label="Dimensions"
                    disabled={disabled}
                  />
                )}

                <Field
                  xs={12}
                  type="textarea"
                  id={`${itemPath}.description`}
                  name={`${itemPath}.description`}
                  mode="dark"
                  label={category === 'unmannedAircraft' ? 'Description of operations' : 'Description'}
                  disabled={disabled}
                />

                {/* #33279: the legacy top-level "Deductible" field is removed for ALL states;
                    only the canonical "All peril deductible" remains, matching Forreston (IL). */}
                <Field
                  xs={6}
                  type="numeric"
                  id={`${itemPath}.limit`}
                  name={`${itemPath}.limit`}
                  mode="dark"
                  label="Coverage Limit"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disabled}
                />
                <BrandFormField
                  xs={6}
                  id={`${itemPath}.extendedDetails.deductibleAllOther`}
                  name={`${itemPath}.extendedDetails.deductibleAllOther`}
                  type="select"
                  options={brandDeductibleOptions}
                  mode="dark"
                  label="All peril deductible"
                  disabled={disabled}
                  riskType={riskType}
                  state={state}
                  coverages={coverages}
                />
              </CollapsibleListItem>
            );
          })}

          {/* add form */}
          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                category: undefined,
                name: undefined,
                // #33279: legacy top-level "Deductible" field removed for ALL states; keep the key
                // null so the built item shape stays stable and never carries a stale $250 default.
                deductible: null,
                limit: undefined,
                description: undefined,
                deductibleAllOther: undefined
              }}
              validateOnChange={true}
              validateOnBlur={true}
              validationSchema={validationSchema()}
              onSubmit={async (val, { resetForm }) => {
                // #33164: skipped on the new-offer form. There the surrounding form is the quote form,
                // still mid-entry, so gating on its validity would block the agent from entering the
                // very first item — and a standalone inland marine offer cannot be rated without one.
                // On the offer/policy routes the gate stays, matching every other sub-risk add form.
                if (validateParentOnAdd) {
                  const parentErrors = await validateForm();
                  // Exclude scheduleCoverages error since adding this item is what resolves it (GitHub issue #29426)
                  const errorsExcludingScheduleCoverages = setIn(parentErrors, fieldName, undefined);
                  if (hasErrorMessages(errorsExcludingScheduleCoverages)) {
                    setErrors(parentErrors);
                    setTouched(setTouchedFromErrors(parentErrors), false);
                    setShowValidationErrorsModal(true);
                    return;
                  }
                }

                const item = val;
                Object.keys(item).forEach((key) => {
                  if (item[key] === '') {
                    item[key] = null;
                  }
                });
                const { category, name, deductible, description, limit, ...rest } = item;
                // #33277: a Miscellaneous boat carries no boat-specific attributes — drop any values
                // entered before switching classification so stale data isn't persisted.
                if (isMiscBoat(category, rest.classification)) {
                  for (const field of BOAT_SPECIFIC_FIELDS) {
                    delete rest[field];
                  }
                }
                const newItem = {
                  category,
                  name,
                  deductible,
                  description,
                  limit,
                  ...(Object.keys(rest).length > 0
                    ? {
                        extendedDetails: {
                          ...rest
                        }
                      }
                    : {})
                };
                const currentScheduleCoverages =
                  stripTypenames(
                    getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.SPP)
                  ) || [];
                const currentSPPItems =
                  stripTypenames(
                    getIn(values, fieldName)?.find((item) => item.coverageType === FarmScheduleCoverageType.SPP)?.items
                  ) || [];
                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.SPP,
                    items: [...currentSPPItems, newItem]
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.SPP}`);

                resetForm();
              }}
            >
              {({ values: formValues, submitForm }) => {
                const category = formValues.category;
                const hideBoatSpecificFields = isMiscBoat(category, formValues.classification);

                return (
                  <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    {/* Top Row: Type of Item + Item Name */}
                    <Field
                      xs={6}
                      type="select"
                      id="category"
                      name="category"
                      mode="dark"
                      label="Type of item"
                      options={brandSpecificTypes}
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id={`${ADD_ID_PREFIX}name`}
                      name="name"
                      mode="dark"
                      label="Item Name"
                      disabled={disabled}
                      helperText="Give this item a small name for reference"
                    />

                    {/* Type-specific fields */}
                    {category === 'golfCart' && (
                      <>
                        <Field
                          xs={6}
                          type="numeric"
                          id={`${ADD_ID_PREFIX}year`}
                          name="year"
                          mode="dark"
                          label="Year"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id={`${ADD_ID_PREFIX}make`}
                          name="make"
                          mode="dark"
                          label="Make"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id={`${ADD_ID_PREFIX}model`}
                          name="model"
                          mode="dark"
                          label="Model"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id={`${ADD_ID_PREFIX}serialNumber`}
                          name="serialNumber"
                          mode="dark"
                          label="Serial Number"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {isAtvLike(category) && (
                      <>
                        <Field
                          xs={4}
                          type="numeric"
                          id={`${ADD_ID_PREFIX}year`}
                          name="year"
                          mode="dark"
                          label="Year"
                          disabled={disabled}
                        />
                        <Field
                          xs={4}
                          type="string"
                          id={`${ADD_ID_PREFIX}make`}
                          name="make"
                          mode="dark"
                          label="Make"
                          disabled={disabled}
                        />
                        <Field
                          xs={4}
                          type="string"
                          id={`${ADD_ID_PREFIX}model`}
                          name="model"
                          mode="dark"
                          label="Model"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id={`${ADD_ID_PREFIX}serialNumber`}
                          name="serialNumber"
                          mode="dark"
                          label="Serial Number"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="select"
                          id="cc"
                          name="cc"
                          mode="dark"
                          label="CC"
                          options={ccOptions}
                          disabled={disabled}
                        />
                      </>
                    )}

                    {category === 'unmannedAircraft' && (
                      <>
                        <Field
                          xs={6}
                          type="numeric"
                          id={`${ADD_ID_PREFIX}year`}
                          name="year"
                          mode="dark"
                          label="Year"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id={`${ADD_ID_PREFIX}make`}
                          name="make"
                          mode="dark"
                          label="Make"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id={`${ADD_ID_PREFIX}model`}
                          name="model"
                          mode="dark"
                          label="Model"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id={`${ADD_ID_PREFIX}serialNumber`}
                          name="serialNumber"
                          mode="dark"
                          label="Serial Number"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id={`${ADD_ID_PREFIX}weight`}
                          name="weight"
                          mode="dark"
                          label="Weight (lbs)"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {category === 'boat' && (
                      <>
                        <Field
                          xs={6}
                          type="select"
                          id="classification"
                          name="classification"
                          mode="dark"
                          label="Classification"
                          options={lookupsJson.farm.boatClassifications}
                          disabled={disabled}
                        />
                        {/* #33277: "Miscellaneous Boating Equipment" is not a specific boat, so hide
                            the boat-specific fields (horsepower/year/length/speed/serial). */}
                        {!hideBoatSpecificFields && (
                          <>
                            <Field
                              xs={6}
                              type="numeric"
                              id={`${ADD_ID_PREFIX}horsepower`}
                              name="horsepower"
                              mode="dark"
                              label="Horsepower"
                              disabled={disabled}
                            />
                            <Field
                              xs={6}
                              type="numeric"
                              id={`${ADD_ID_PREFIX}year`}
                              name="year"
                              mode="dark"
                              label="Year"
                              disabled={disabled}
                            />
                            <Field
                              xs={6}
                              type="numeric"
                              id={`${ADD_ID_PREFIX}lengthFt`}
                              name="lengthFt"
                              mode="dark"
                              label="Length (ft)"
                              disabled={disabled}
                            />
                            <Field
                              xs={6}
                              type="numeric"
                              id={`${ADD_ID_PREFIX}milesPerHour`}
                              name="milesPerHour"
                              mode="dark"
                              label="Miles Per Hour"
                              disabled={disabled}
                            />
                            <Field
                              xs={6}
                              type="string"
                              id={`${ADD_ID_PREFIX}serialNumber`}
                              name="serialNumber"
                              mode="dark"
                              label="Serial Number"
                              disabled={disabled}
                            />
                          </>
                        )}
                      </>
                    )}

                    {category === 'boatDock' && (
                      <Field
                        xs={12}
                        type="string"
                        id={`${ADD_ID_PREFIX}dimensions`}
                        name="dimensions"
                        mode="dark"
                        label="Dimensions"
                        disabled={disabled}
                      />
                    )}

                    {/* Bottom Row: Coverage Limit + All Peril Deductible. #33279: the legacy
                        top-level "Deductible" field is removed for ALL states; only the canonical
                        "All peril deductible" remains, matching Forreston (IL). */}
                    {category && (
                      <>
                        <Field
                          xs={12}
                          type="textarea"
                          id={`${ADD_ID_PREFIX}description`}
                          name="description"
                          mode="dark"
                          label={category === 'unmannedAircraft' ? 'Description of operations' : 'Description'}
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          id={`${ADD_ID_PREFIX}limit`}
                          name="limit"
                          mode="dark"
                          label="Coverage Limit"
                          format={{ thousandSeparator: true, prefix: '$' }}
                          disabled={disabled}
                        />
                        <BrandFormField
                          xs={6}
                          id="deductibleAllOther"
                          name="deductibleAllOther"
                          type="select"
                          options={brandDeductibleOptions}
                          mode="dark"
                          label="All peril deductible"
                          disabled={disabled}
                          riskType={riskType}
                          state={state}
                          coverages={coverages}
                        />
                      </>
                    )}

                    {category && (
                      <Grid container alignItems="center" className={classes.singleBottom}>
                        {alertMessages[category] && (
                          <div className={classes.alert}>
                            <img src={alertIcon} className={classes.alertIcon} alt="Alert Icon" />
                            <div className={classes.alertMessage}>{alertMessages[category]}</div>
                          </div>
                        )}
                        <Button
                          type="button"
                          mode="big"
                          variant="contained"
                          color="secondary"
                          className={classes.button}
                          disabled={disabled}
                          onClick={submitForm}
                        >
                          Add Item
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SectionWrapper>
    </>
  );
};

InlandMarineForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  riskType: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  // Brand-option state. Overrides the location-0 / correctedAddress fallbacks. (#33164)
  state: PropTypes.string,
  // Render the scheduled-items section without its collapser heading and chevron — for the
  // standalone inland marine risk card, whose only content is this section. (#33164)
  hideCollapser: PropTypes.bool,
  // Whether adding an item first requires the surrounding form to be valid. True on the offer and
  // policy routes (fix the form before editing coverages). False on the new-offer form, where the
  // quote is still being filled in and unrelated incomplete sections must not block item entry.
  // (#33164)
  validateParentOnAdd: PropTypes.bool
};

InlandMarineForm.defaultProps = {
  disabled: false,
  hideCollapser: false,
  validateParentOnAdd: true
};

export default observer(InlandMarineForm);
